<script lang="ts" setup>
import {
  useAppKit,
  useAppKitAccount,
  useAppKitEvents,
  useDisconnect,
  useWalletInfo,
} from "@reown/appkit/vue";
import {
  useSignMessage,
  useConfig,
  useConnectorClient,
  useAccount,
} from "@wagmi/vue";
import {
  signMessage as wagmiSignMessage,
  getConnectorClient,
} from "@wagmi/core";
import bs58 from "bs58";

const emit = defineEmits(["verified", "error"]);

interface Props {
  element: "button" | "a";
  walletId: string;
  caipAddress: string;
}

const props = defineProps<Props>();

const modal = useAppKit();
const account = useAppKitAccount();
const events = useAppKitEvents();
const wagmiConfig = useConfig();
const { signMessageAsync } = useSignMessage();
const lock = ref({});

// Simple lock mechanism
const globalLock = { current: null as any };

async function startMessageSigning() {
  console.log("startMessageSigning called", {
    caipAddress: props.caipAddress,
    accountCaipAddress: account.value.caipAddress,
    accountAddress: account.value.address,
    isConnected: account.value.isConnected,
  });

  globalLock.current = lock.value;

  // Check if the correct wallet is connected
  if (props.caipAddress !== account.value.caipAddress) {
    if (account.value.caipAddress) {
      console.log("Disconnecting current wallet");
      const { disconnect } = useDisconnect();
      await disconnect();
    }
    console.log("Opening modal to connect wallet");
    modal.open();
  } else {
    console.log("Wallet already connected, calling signMessage");
    signMessage();
  }
}

async function signMessage() {
  console.log("signMessage function called");

  trackEvent("user_verify_wallet_sign_message_attempted", {
    trigger: "tile_verify_wallet_ownership_modal_sign_message_clicked",
    wallet_id: props.walletId,
  });

  const isEthereumWallet = props.caipAddress.startsWith("eip155:");
  console.log("isEthereumWallet:", isEthereumWallet);

  try {
    console.log("Calling startWalletVerification API");
    const response = await apiService.startWalletVerification({
      walletId: props.walletId,
      textToSignTemplate:
        "I certify that I am not a resident or citizen of North Korea, Iran, Syria, Cuba, or Myanmar, and I acknowledge that providing false information may have legal consequences (transaction __TOKEN__)",
    });
    const { fullTextToSign, verificationId } = response.data;
    console.log("Got verification response:", {
      verificationId,
      fullTextToSign,
    });

    let signature: string;

    successMessage("Please sign the message to verify wallet ownership.");

    if (isEthereumWallet) {
      console.log("Attempting Ethereum signature");
      console.log("wagmiConfig:", wagmiConfig);
      console.log("account.value:", account.value);

      // For Ethereum wallets, use the wallet provider directly via personal_sign
      try {
        console.log("Getting connector client");
        const client = await getConnectorClient(wagmiConfig);
        console.log("Got connector client:", client);

        if (!client) {
          throw new Error("No connector client available");
        }

        // Get the EIP-1193 provider from the client
        const provider = client.transport;
        console.log("Got provider:", provider);

        // Convert message to hex
        const messageHex = `0x${Array.from(
          new TextEncoder().encode(fullTextToSign),
        )
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("")}`;

        console.log("Message hex:", messageHex);
        console.log("Requesting signature via personal_sign");

        if (!account.value.address) {
          throw new Error("Account address is not available");
        }

        // Use personal_sign directly
        // Type assertion needed due to strict wagmi client typing
        const signResponse = await client.request({
          method: "personal_sign",
          params: [messageHex, account.value.address] as [string, string],
        } as any);

        console.log("Got signature response:", signResponse);
        signature = signResponse as string;
      } catch (signError) {
        console.error("Ethereum signing error:", signError);
        throw signError;
      }
    } else {
      // For Solana wallets, use the window.solana provider directly
      const solanaProvider: any = (window as any).solana;
      if (!solanaProvider?.signMessage) {
        throw new Error("Solana wallet provider does not support signing");
      }
      const signResponse = await solanaProvider.signMessage(
        new TextEncoder().encode(fullTextToSign),
      );
      // For Solana, encode the signature with bs58
      signature = bs58.encode(signResponse);
    }

    await apiService.completeWalletVerification({ verificationId, signature });
    successMessage("Wallet ownership verified successfully");
    globalLock.current = null;

    trackEvent("user_verify_wallet_sign_message_success", {
      trigger: "",
      wallet_id: props.walletId,
    });
    emit("verified");
  } catch (err) {
    console.error("Error in signMessage:", err);
    console.error("Error details:", {
      message: (err as any)?.message,
      stack: (err as any)?.stack,
      name: (err as any)?.name,
    });

    if (typeof (err as any)?.message === "string") {
      if (
        // @ts-expect-error
        err.message.includes(
          "[unsupportedOperation] Signing off chain messages with Ledger is not yet supported.",
        )
      ) {
        trackEvent("user_verify_wallet_sign_message_failure", {
          trigger: "async_signing_failure",
          wallet_id: props.walletId,
          error: "Ledger is not supported",
        });

        errorMessage("Signing off messages with Ledger is not supported");
        emit("error", { reason: "ledger" });
        return;
      }
    }

    trackEvent("user_verify_wallet_sign_message_failure", {
      trigger: "async_signing_failure",
      wallet_id: props.walletId,
      // @ts-expect-error
      error: err.message,
    });

    errorMessage(
      "Failed to verify wallet ownership. Please try again, and contact support if the issue persists.",
    );
  }
}

watch(events, async (newEvents) => {
  console.log("VerifyWallet event watcher", {
    walletId: props.walletId,
    newEvents: JSON.parse(JSON.stringify(newEvents)),
  });

  if (
    newEvents.data.event === "CONNECT_SUCCESS" &&
    globalLock.current === lock.value
  ) {
    globalLock.current = null;
    if (props.caipAddress !== account.value.caipAddress) {
      errorMessage("Connected to a different wallet than expected.");
      const { disconnect } = useDisconnect();
      await disconnect();
    } else {
      signMessage();
    }
  }
});
</script>

<template>
  <a
    v-if="props.element === 'a'"
    v-bind="$attrs"
    class="cursor-pointer hover:underline"
    @click="startMessageSigning()"
  >
    <slot />
  </a>
  <button
    v-if="props.element === 'button'"
    v-bind="$attrs"
    @click="startMessageSigning()"
  >
    <slot />
  </button>
</template>
